import { ArrowLeft, Download, Mail, Phone, Search, UserRound, Users } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';
import { useMemo, useState } from 'react';
import Spinner from '../../components/Spinner';
import { SERVER_URL } from '../../api/jobBoardApi';
import { useApplicants, useUpdateApplicantStatus } from '../../hooks/useJobBoard';
import { ApplicationStatus } from '../../types/jobBoard';

const statuses: ApplicationStatus[] = [
  "New",
  "Reviewed",
  "Shortlisted",
  "Rejected",
  "Hired",
];

const label = (app: {
  fullName?: string;
  firstName?: string;
  lastName?: string;
}) =>
  app.fullName ??
  ([app.firstName, app.lastName]
    .filter(Boolean)
    .join(" ") || "Applicant");

export default function AdminApplicantsPage() {
  const { id: jobId = "" } = useParams();

//   const job = useJob(jobId);
  const applicants = useApplicants(jobId);
  const update = useUpdateApplicantStatus(jobId);

  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("All");

  const filtered = useMemo(
    () =>
      (applicants.data ?? []).filter(
        (app) =>
          `${label(app)} ${app.emailAddress}`
            .toLowerCase()
            .includes(search.toLowerCase()) &&
          (status === "All" ||
            app.applicationStatus === status)
      ),
    [applicants.data, search, status]
  );

  if (applicants.isLoading) {
    return (
      <div className="flex justify-center p-12">
        <Spinner />
      </div>
    );
  }
  return (
    <div className="space-y-6 text-left">
        <Link
        to={`/admin/job-board/jobs/${jobId}`}
        className="inline-flex items-center gap-2 text-sm font-medium text-strivre-burgundy hover:underline"
        >
        <ArrowLeft size={16} />
        Back to Job
        </Link>

        <header>
        <h1 className="flex items-center gap-2 text-2xl font-bold text-strivre-navy">
            <Users
            size={22}
            className="text-strivre-burgundy"
            />
            Applicants
        </h1>

        <p className="mt-1 text-sm text-strivre-mutedtext">
            Review applications, update candidate statuses, and download resumes.
        </p>
        </header>

        <div className="rounded-2xl border border-strivre-lightnavy bg-white p-4">
        <div className="flex flex-col gap-3 sm:flex-row">
            <div className="relative flex-1">
            <Search
                size={17}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
            />

            <input
                className="input-base pl-10"
                placeholder="Search applicants..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
            />
            </div>

            <select
            className="input-base sm:w-48"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            >
            <option value="All">All Statuses</option>

            {statuses.map((item) => (
                <option key={item} value={item}>
                {item}
                </option>
            ))}
            </select>
        </div>
        </div>

        <div className="overflow-hidden rounded-2xl border border-strivre-lightnavy bg-white shadow-sm">
        <div className="overflow-x-auto">
            <table className="w-full min-w-[760px] text-left text-sm">
            <thead className="bg-slate-50">
                <tr>
                <th className="px-5 py-4 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Applicant
                </th>

                <th className="px-5 py-4 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Contact
                </th>

                <th className="px-5 py-4 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Applied
                </th>

                <th className="px-5 py-4 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Status
                </th>

                <th className="px-5 py-4 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Resume
                </th>
                </tr>
            </thead>

            <tbody className="divide-y divide-slate-100">
                {filtered.map((app) => (
                <tr
                    key={app.applicantId}
                    className="hover:bg-slate-50"
                >
                    <td className="px-5 py-4">
                    <div>
                        <p className="font-semibold text-slate-900">
                        {label(app)}
                        </p>

                        <p className="mt-1 text-xs text-slate-400">
                        ID: {app.applicantId.slice(0, 8)}
                        </p>
                    </div>
                    </td>

                    <td className="px-5 py-4">
                    <p className="flex items-center gap-2 text-slate-700">
                        <Mail size={14} />
                        {app.emailAddress}
                    </p>

                    <p className="mt-1 flex items-center gap-2 text-xs text-slate-500">
                        <Phone size={13} />
                        {app.contactNumber ?? "-"}
                    </p>
                    </td>

                    <td className="px-5 py-4 text-slate-600">
                    {app.applicationDate ?? app.appliedDate
                        ? new Date(
                            (app.applicationDate ??
                            app.appliedDate) as string
                        ).toLocaleDateString()
                        : "-"}
                    </td>

                    <td className="px-5 py-4">
                    <select
                        disabled={update.isPending}
                        value={
                        statuses.includes(app.applicationStatus)
                            ? app.applicationStatus
                            : "New"
                        }
                        onChange={(e) => {
                        if (
                            window.confirm(
                            "Update this applicant status?"
                            )
                        ) {
                            update.mutate({
                            applicantId: app.applicantId,
                            status:
                                e.target.value as ApplicationStatus,
                            });
                        }
                        }}
                        className="input-base w-40 py-2 text-xs"
                    >
                        {statuses.map((item) => (
                        <option
                            key={item}
                            value={item}
                        >
                            {item}
                        </option>
                        ))}
                    </select>
                    </td>

                    <td className="px-5 py-4">
                        {app.resumeUrl || app.resumePath ? (
                            <a
                            href={
                                (app.resumeUrl ?? app.resumePath)?.startsWith("http")
                                ? (app.resumeUrl ?? app.resumePath)
                                : `${SERVER_URL}/${app.resumeUrl ?? app.resumePath}`
                            }
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-2 rounded-lg bg-strivre-lightburgundy px-3 py-2 text-sm font-semibold text-strivre-burgundy transition hover:opacity-80"
                            >
                            <Download size={15} />
                            Download
                            </a>
                        ) : (
                            <span className="text-slate-400">
                            Unavailable
                            </span>
                        )}
                    </td>
                </tr>
                ))}
            </tbody>
            </table>
        </div>

        {!filtered.length && (
            <div className="p-16 text-center">
            <UserRound
                size={40}
                className="mx-auto text-slate-300"
            />

            <h3 className="mt-4 text-lg font-semibold text-slate-800">
                No Applicants Found
            </h3>

            <p className="mt-2 text-sm text-slate-500">
                No applicants match the selected filters.
            </p>
            </div>
        )}
        </div>
    </div>
  );
}